import { Button, Checkbox, Icon, Modal, Table, Tooltip } from 'antd'
import dayjs from 'dayjs'
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import {
  FoodSafetyHeaderRow,
  FoodSafetyHeroBand,
  FoodSafetyPageTitle,
} from 'src/components/food-safety/FoodSafetyPageChrome'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import NoDataScreen from 'src/components/shared/no-data-screen/NoDataScreen'
import ProductImage from 'src/components/shared/ProductImage'
import SearchBar from 'src/components/shared/SearchBar'
import FadeIn from 'src/components/styled/FadeIn'
import { stickyActionsColumn } from 'src/components/styled/horizontalScroll'
import { RESPONSIVE_TABLE_SCROLL } from 'src/utils/responsiveTable'
import {
  CATALOGUE_OPTIONAL_COLUMN_DEFS,
  filterCatalogueVisibleColumns,
  listCatalogueOptionalColumns,
  loadCatalogueHiddenColumns,
  saveCatalogueHiddenColumns,
  toggleCatalogueHiddenColumn,
} from 'src/utils/catalogueColumns'
import Loading from 'src/components/styled/Loading'
import GrowthEngineFollowUpStrip from 'src/components/growthEngine/GrowthEngineFollowUpStrip'
import { VAT_RATE } from 'src/config'
import {
  performSearch,
  setSelectedProductType,
  setSelectedSupplierTitle,
  setTerm,
} from 'src/features/CatalogueSearchSlice'
import {
  addVenueProduct,
  fetchVenueProducts,
} from 'src/features/VenueProductsSlice'
import { getVenueSuppliers } from 'src/features/VenueSuppliersSlice'
import AddVenueProductModal from 'src/screens/catalogue/AddVenueProductModal'
import ReportProductIssueButton from 'src/components/productIssue/ReportProductIssueButton'
import { PRODUCT_ISSUE_SOURCE } from 'src/utils/productIssue'
import {
  getActiveSupplierTitles,
  getRecentlyConnectedSupplier,
} from 'src/utils/catalogueSupplierUtils'
import { PAR_LEVEL_LIMIT_ERROR_CODE } from 'src/utils/parLevelPaywall'
import {
  readCatalogueCompareContext,
  readCatalogueUrlSearch,
} from 'src/utils/ollieActionDeepLinks'
import {
  buildOllieRelatedSearchTerm,
  filterOllieCompareAlternatives,
  formatCatalogueComparableUnitPrice,
  OLLIE_COMPARE_COPY,
} from 'src/utils/ollieCompareSuppliers'
import {
  buildCatalogueAddPayload,
  buildCatalogueProductGateAddPayload,
  getCatalogueAddButtonState,
  getCatalogueProductSupplierTitle, // list Add must not crash if supplier is an id
  shouldUseCatalogueProductGateQuickAdd,
} from 'src/utils/growthEngineCatalogueQuickAdd'
import styled from 'styled-components/macro'
import { createOrder } from '../../features/OrdersSlice'
import BasketFunctionality from '../basketFunctionality/BasketFunctionality'
import useIsBlocked from 'src/hooks/useIsBlocked'
import PermissionBlockScreen from 'src/components/shared/PermissionBlockScreen'

const Catalogue = props => {
  const blocked = useIsBlocked('catalogue')

  if (blocked) {
    return <PermissionBlockScreen />
  }

  const venueId = props.match.params.venueId
  const dispatch = useDispatch()
  const isCatalogueProductGate = shouldUseCatalogueProductGateQuickAdd(
    props.location?.pathname,
    props.location?.search,
  )

  const searchProducts = useSelector(state => state.catalogueSearch.products)
  const searchPage = useSelector(state => state.catalogueSearch.page)
  const searchPageSize = useSelector(state => state.catalogueSearch.pageSize)
  const searchTotal = useSelector(state => state.catalogueSearch.total)
  const searchLoading = useSelector(state => state.catalogueSearch.loading)
  const hasCompletedSearch = useSelector(
    state => state.catalogueSearch.hasCompletedSearch,
  )
  const searchTerm = useSelector(state => state.catalogueSearch.term)
  const selectedSupplierTitle = useSelector(
    state => state.catalogueSearch.selectedSupplierTitle,
  )
  const selectedProductType = useSelector(
    state => state.catalogueSearch.selectedProductType,
  )
  const supplierFilterName = useSelector(
    state => state.filter.catalogue.supplier,
  )
  const { isAdmin } = useSelector(state => state.auth.account)
  const { isPricesHidden, dataplusIntegration } = useSelector(
    state => state.venue.venue,
  )
  const currentPlan = useSelector(
    state => state.venue.venue?.usageLimits?.currentPlan ?? 'Free',
  )
  const venueSuppliers = useSelector(
    state => state.venueSuppliers.venueSuppliers,
  )
  const areVenueSuppliersLoading = useSelector(
    state => state.venueSuppliers.isLoading,
  )
  const isSomeVenueSupplierActive = useSelector(
    state => state.venueSuppliers.isSomeVenueSupplierActive,
  )
  const venueProducts = useSelector(state => state.venueProducts.products)

  const [showModal, setShowModal] = useState(null)
  const [savingProduct, setSavingProduct] = useState(false)
  const [addingProductId, setAddingProductId] = useState(null)
  const [selectedProduct, setSelectedProduct] = useState({
    title: '',
    zone: '',
    secondaryZone: '',
    supplier: '',
    supplierTitle: '',
    isFavourite: false,
  })
  const [addToBasket, setAddToBasket] = useState(false)
  const [bannerDismissed, setBannerDismissed] = useState(false)
  const [bannerExpanded, setBannerExpanded] = useState(false)
  const [hideAlreadyOnList, setHideAlreadyOnList] = useState(false)
  const [columnPickerOpen, setColumnPickerOpen] = useState(false)
  const [hiddenColumnKeys, setHiddenColumnKeys] = useState(() =>
    loadCatalogueHiddenColumns(venueId),
  )
  const previousVenueIdRef = useRef(venueId)

  useEffect(() => {
    if (venueProducts.length === 0 && venueId)
      dispatch(fetchVenueProducts(venueId))
  }, [dispatch, venueId, venueProducts.length])

  useEffect(() => {
    if (venueId) dispatch(getVenueSuppliers(venueId))
  }, [dispatch, venueId])

  // Clear stale supplier filter / search when switching venues
  useEffect(() => {
    if (previousVenueIdRef.current === venueId) return
    previousVenueIdRef.current = venueId
    if (supplierFilterName?.length > 0) return
    dispatch(setSelectedSupplierTitle(''))
    dispatch(setSelectedProductType(''))
  }, [dispatch, venueId, supplierFilterName])

  useEffect(() => {
    setHiddenColumnKeys(loadCatalogueHiddenColumns(venueId))
  }, [venueId])

  const showCataloguePriceColumn = (!isPricesHidden && isAdmin) || !isAdmin
  const cataloguePickerRows = listCatalogueOptionalColumns(
    CATALOGUE_OPTIONAL_COLUMN_DEFS,
    {
      showPrice: showCataloguePriceColumn,
      showGln: Boolean(dataplusIntegration),
    },
  )
  const pickerHiddenKeys = Array.isArray(hiddenColumnKeys)
    ? hiddenColumnKeys
    : []
  const handleToggleCatalogueColumn = columnKey => {
    const next = toggleCatalogueHiddenColumn(pickerHiddenKeys, columnKey)
    saveCatalogueHiddenColumns(venueId, next)
    setHiddenColumnKeys(next)
  }

  const venueProductsIds = useMemo(
    () =>
      venueProducts
        .filter(prod => prod.product)
        .map(vProduct => vProduct.product._id),
    [venueProducts],
  )

  const displayProducts = useMemo(() => {
    const products = searchProducts || []
    if (!hideAlreadyOnList) return products
    return products.filter(product => !venueProductsIds.includes(product._id))
  }, [searchProducts, hideAlreadyOnList, venueProductsIds])

  const hiddenOnPageCount = useMemo(() => {
    if (!hideAlreadyOnList || !searchProducts?.length) return 0
    return searchProducts.length - displayProducts.length
  }, [hideAlreadyOnList, searchProducts, displayProducts.length])

  const urlSearch = readCatalogueUrlSearch(props.location?.search)
  const compareContext = useMemo(
    () => readCatalogueCompareContext(props.location?.search),
    [props.location?.search],
  )

  const hasActiveQuery = Boolean(
    searchTerm?.trim() || selectedSupplierTitle || selectedProductType,
  )
  const compareAlternatives = useMemo(
    () =>
      compareContext
        ? filterOllieCompareAlternatives(searchProducts || [], {
            compareProductId: compareContext.productId,
            compareProductTitle: compareContext.productTitle,
          })
        : null,
    [compareContext, searchProducts],
  )
  const showCompareEmpty =
    Boolean(compareContext) &&
    !searchLoading &&
    hasActiveQuery &&
    Array.isArray(searchProducts) &&
    compareAlternatives.length === 0
  const showEmptyResults =
    !searchLoading &&
    Array.isArray(searchProducts) &&
    searchProducts.length === 0 &&
    hasActiveQuery &&
    !compareContext
  const showAllHiddenOnPage =
    !searchLoading &&
    hideAlreadyOnList &&
    Array.isArray(searchProducts) &&
    searchProducts.length > 0 &&
    displayProducts.length === 0 &&
    !showCompareEmpty

  const emptyResultsMessage = () => {
    const term = searchTerm?.trim()
    const parts = []
    if (term) parts.push(`matching “${term}”`)
    if (selectedProductType) parts.push(`type “${selectedProductType}”`)
    if (selectedSupplierTitle) parts.push(`from ${selectedSupplierTitle}`)

    if (parts.length) {
      return `No products ${parts.join(
        ' ',
      )}. Try clearing a filter or searching something else.`
    }
    return 'No products found.'
  }

  const handleSearchRelatedProducts = () => {
    const related =
      buildOllieRelatedSearchTerm(compareContext?.productTitle) ||
      compareContext?.searchTerm
    if (!related || !props.history) return
    props.history.replace({
      pathname: props.location.pathname,
      search: `?search=${encodeURIComponent(related)}`,
    })
  }

  const tableProducts = compareContext
    ? hideAlreadyOnList
      ? compareAlternatives.filter(
          product => !venueProductsIds.includes(product._id),
        )
      : compareAlternatives
    : displayProducts

  const activeSupplierTitles = useMemo(
    () => getActiveSupplierTitles(venueSuppliers),
    [venueSuppliers],
  )

  const recentlyConnectedSupplier = useMemo(
    () => getRecentlyConnectedSupplier(venueId),
    [venueId],
  )

  useEffect(() => {
    if (addToBasket) {
      // eslint-disable-next-line
      const { _id, ...res } = venueProducts.at(-1)
      dispatch(
        createOrder({
          ...res,
          quantity: selectedProduct.defaultQuantity || 1,
          OMSSpecialPrice:
            selectedProduct.OMSSpecialPrice !== 'NaN'
              ? selectedProduct.OMSSpecialPrice
              : 0,
          method: 'create',
        }),
      )
      setAddToBasket(false)
    }
  }, [venueProducts])

  // Search once suppliers are ready (includes Your-suppliers redirect after SearchBar sets filter)
  useEffect(() => {
    if (!venueId || areVenueSuppliersLoading) return
    // null = suppliers not loaded yet for this session
    if (isSomeVenueSupplierActive == null) return
    if (isSomeVenueSupplierActive === false) return
    // ?search= (e.g. Ollie's Compare suppliers) searches every supplier.
    if (urlSearch) {
      dispatch(setTerm(urlSearch))
      dispatch(setSelectedSupplierTitle(''))
      dispatch(setSelectedProductType(''))
    }
    dispatch(
      performSearch({
        venueId,
        pageNumber: 1,
        pageSize: searchPageSize || 50,
      }),
    )
  }, [venueId, areVenueSuppliersLoading, isSomeVenueSupplierActive, urlSearch])

  const dismissBanner = () => {
    setBannerDismissed(true)
  }

  const handleOnOk = async () => {
    if (!showModal?._id || savingProduct) return
    setSavingProduct(true)
    try {
      const result = await dispatch(
        addVenueProduct(
          buildCatalogueAddPayload(showModal, venueId, selectedProduct),
        ),
      )
      if (result && result !== PAR_LEVEL_LIMIT_ERROR_CODE) {
        setShowModal(null)
      }
    } finally {
      setSavingProduct(false)
    }
  }

  const handleCatalogueProductGateAdd = async product => {
    if (!product?._id || addingProductId === product._id) return
    setAddingProductId(product._id)
    try {
      await dispatch(
        addVenueProduct(buildCatalogueProductGateAddPayload(product, venueId)),
      )
    } finally {
      setAddingProductId(current => (current === product._id ? null : current))
    }
  }

  const handleAddClick = product => {
    if (isCatalogueProductGate) {
      handleCatalogueProductGateAdd(product)
      return
    }
    setSelectedProduct({
      title: product.title,
      type: product.type,
      UoM: product.UoM,
      supplier: product.supplier,
      supplierTitle: getCatalogueProductSupplierTitle(product),
    })
    setShowModal(product)
  }

  return (
    <Wrapper>
      <FoodSafetyHeroBand>
        <FoodSafetyHeaderRow>
          <div>
            <FoodSafetyPageTitle>Catalogue</FoodSafetyPageTitle>
            <HeroIntro>
              Browse products from all your active suppliers
            </HeroIntro>
          </div>
          <Link to={`/v/${venueId}/suppliers`}>
            <Button type='primary'>Add supplier</Button>
          </Link>
        </FoodSafetyHeaderRow>
      </FoodSafetyHeroBand>
      <GrowthEngineFollowUpStrip
        venueId={venueId}
        currentPlan={currentPlan}
        surface='catalogue'
      />
      {!bannerDismissed && (
        <TipBanner>
          <TipContent>
            <TipHeadline>
              Catalogue = discover. Shopping list = what staff can order.
            </TipHeadline>
            <TipText>
              Browse the full range here, then add only the products you want
              for recipes and reordering. That keeps orders focused — and Order
              Only users never see the catalogue.
            </TipText>
            <WhyToggle
              type='link'
              onClick={() => setBannerExpanded(expanded => !expanded)}
            >
              {bannerExpanded ? 'Hide why this works' : 'Why this works'}
            </WhyToggle>
            {bannerExpanded && (
              <WhyExpanded>
                <p>
                  Suppliers can list thousands of products. Ordering from the
                  full catalogue every time leads to product blindness — the
                  wrong items slip through, and costs creep up.
                </p>
                <p>
                  Use the catalogue to choose what you cook and reorder around,
                  then add those products to your shopping list. Day-to-day
                  ordering stays fast and consistent.
                </p>
                <p>
                  It also protects the business: users with the Order Only role
                  can’t access the catalogue, so chefs stick to the approved
                  list instead of going rogue.
                </p>
              </WhyExpanded>
            )}
          </TipContent>
          <TipDismiss
            type='link'
            aria-label='Dismiss catalogue tip'
            onClick={dismissBanner}
          >
            <Icon type='close' />
          </TipDismiss>
        </TipBanner>
      )}
      {areVenueSuppliersLoading || isSomeVenueSupplierActive == null ? (
        <Loading />
      ) : isSomeVenueSupplierActive === false ? (
        <NoDataScreen page='catalogue' />
      ) : (
        <Fragment>
          <ListCard>
            <SearchBar
              venueId={venueId}
              supplierTitles={activeSupplierTitles}
              recentSupplier={recentlyConnectedSupplier}
            />
            {compareContext ? (
              <CompareBanner>
                <CompareBannerTitle>
                  {OLLIE_COMPARE_COPY.bannerLead}{' '}
                  <strong>{compareContext.productTitle}</strong>
                </CompareBannerTitle>
                <CompareBannerHint>
                  {OLLIE_COMPARE_COPY.bannerHint}
                </CompareBannerHint>
              </CompareBanner>
            ) : null}
            <ResultsToolbar>
              <Checkbox
                checked={hideAlreadyOnList}
                onChange={event => setHideAlreadyOnList(event.target.checked)}
              >
                Hide items already on my shopping list
              </Checkbox>
              {hideAlreadyOnList && hiddenOnPageCount > 0 && (
                <ToolbarHint>
                  {hiddenOnPageCount} hidden on this page
                </ToolbarHint>
              )}
              <Button
                data-testid='catalogue-columns-button'
                onClick={() => setColumnPickerOpen(true)}
              >
                <Icon type='table' /> Edit columns
              </Button>
            </ResultsToolbar>
            {showCompareEmpty ? (
              <EmptyResults>
                <EmptyTitle>No comparable alternatives</EmptyTitle>
                <EmptyBody>{OLLIE_COMPARE_COPY.empty}</EmptyBody>
                <EmptyActions>
                  <Button type='primary' onClick={handleSearchRelatedProducts}>
                    {OLLIE_COMPARE_COPY.searchRelated} →
                  </Button>
                </EmptyActions>
              </EmptyResults>
            ) : showEmptyResults ? (
              <EmptyResults>
                <EmptyTitle>No matching products</EmptyTitle>
                <EmptyBody>{emptyResultsMessage()}</EmptyBody>
              </EmptyResults>
            ) : showAllHiddenOnPage ? (
              <EmptyResults>
                <EmptyTitle>Nothing new on this page</EmptyTitle>
                <EmptyBody>
                  All products on this page are already on your shopping list.
                  Turn off the filter above or go to the next page to keep
                  browsing.
                </EmptyBody>
              </EmptyResults>
            ) : !hasCompletedSearch ||
              (searchLoading && !(searchProducts || []).length) ? (
              <Loading />
            ) : (
              ((searchProducts || []).length > 0 || searchLoading) && (
                <StyledTable
                  scroll={RESPONSIVE_TABLE_SCROLL}
                  dataSource={tableProducts.map(product => ({
                    key: product._id,
                    ...product,
                  }))}
                  pagination={{
                    position: 'both',
                    pageSize: searchPageSize,
                    pageSizeOptions: ['10', '20', '50', '100', '250'],
                    showSizeChanger: true,
                    current: searchPage,
                    total: searchTotal,
                    onChange: page =>
                      dispatch(
                        performSearch({
                          venueId,
                          pageNumber: page,
                          pageSize: searchPageSize,
                        }),
                      ),
                    onShowSizeChange: (_, pageSize) =>
                      dispatch(
                        performSearch({ venueId, pageNumber: 1, pageSize }),
                      ),
                    showTotal: (total, range) =>
                      `${range[0]}-${range[1]} of ${total} results`,
                  }}
                  loading={searchLoading}
                  locale={{
                    emptyText: searchLoading ? 'Loading products…' : 'No Data',
                  }}
                  rowClassName={product => {
                    let inVenueCat = venueProductsIds.includes(product._id)
                    return inVenueCat ? 'active' : ''
                  }}
                  columns={filterCatalogueVisibleColumns(
                    [
                      {
                        title: '',
                        dataIndex: 'cloudinaryImageId',
                        key: 'cloudinaryImageId',
                        render: imageId => {
                          return <ProductImage imageId={imageId} />
                        },
                      },
                      {
                        title: 'Title',
                        dataIndex: 'title',
                        key: 'title',
                        className: 'restrictShrink',
                        sorter: (a, b) =>
                          String(a.title)
                            .trim()
                            .localeCompare(String(b.title).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      {
                        title: 'Size',
                        dataIndex: 'size',
                        key: 'size',
                        className: 'restrictShrink',
                        sorter: (a, b) =>
                          String(a.size)
                            .trim()
                            .localeCompare(String(b.size).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      (!isPricesHidden && isAdmin) || !isAdmin
                        ? {
                            title: 'Price',
                            dataIndex: 'price',
                            key: 'price',
                            className: 'restrictShrink',
                            render: (text, item) => {
                              let diffSymbol = ''
                              if (
                                Object.prototype.hasOwnProperty.call(
                                  item,
                                  'priceHistory',
                                ) &&
                                item.priceHistory.length
                              ) {
                                const priceLastUpdated = dayjs(
                                  item.priceHistory[0].updatedAt,
                                )
                                const diff = dayjs().diff(
                                  priceLastUpdated,
                                  'days',
                                )
                                if (diff < 14) {
                                  diffSymbol = '▲'
                                  if (
                                    item.price <
                                    item.priceHistory[0].formerPrice
                                  ) {
                                    diffSymbol = '▼'
                                  }
                                }
                              }
                              return (
                                <span className='price-change'>
                                  £{Number(text).toFixed(2)}
                                  {item.hasVat ? ' + VAT' : ''}
                                  {diffSymbol && (
                                    <span className='diff-symbol'>
                                      {diffSymbol}
                                      <span className='tooltip'>
                                        Price has changed in the last 14 days
                                      </span>
                                    </span>
                                  )}
                                </span>
                              )
                            },
                            sorter: (a, b) =>
                              (a.hasVat ? VAT_RATE * a.price : a.price) -
                              (b.hasVat ? VAT_RATE * b.price : b.price),
                            sortDirections: ['ascend', 'descend'],
                          }
                        : {},
                      compareContext &&
                      ((!isPricesHidden && isAdmin) || !isAdmin)
                        ? {
                            title: (
                              <Tooltip
                                title={OLLIE_COMPARE_COPY.comparablePriceHint}
                              >
                                {OLLIE_COMPARE_COPY.comparablePrice}
                              </Tooltip>
                            ),
                            key: 'comparableUnitPrice',
                            className: 'restrictShrink',
                            render: (_, item) =>
                              formatCatalogueComparableUnitPrice(item) || '—',
                          }
                        : {},
                      {
                        title: 'UoM',
                        dataIndex: 'UoM',
                        key: 'UoM',
                        className: 'restrictShrink',
                        render: text => {
                          return text || ''
                        },
                        sorter: (a, b) =>
                          String(a.size)
                            .trim()
                            .localeCompare(String(b.size).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      {
                        title: 'Priced by',
                        dataIndex: 'pricedBy',
                        key: 'pricedBy',
                        className: 'restrictShrink',
                        render: (text, prod) => {
                          return text
                            ? text === 'Each'
                              ? `${text}`
                              : `per ${text}`
                            : prod.UoM
                            ? prod.UoM === 'Each'
                              ? `${prod.UoM}`
                              : `per ${prod.UoM}`
                            : ''
                        },
                      },
                      {
                        title: 'Supplier',
                        dataIndex: 'supplier.title',
                        key: 'supplier.title',
                        className: 'restrictShrink',
                      },
                      {
                        title: 'SKU',
                        dataIndex: 'sku',
                        key: 'sku',
                        className: 'restrictShrink',
                        sorter: (a, b) =>
                          String(a.sku)
                            .trim()
                            .localeCompare(String(b.sku).trim()),
                        sortDirections: ['ascend', 'descend'],
                      },
                      dataplusIntegration
                        ? {
                            title: 'GLN',
                            dataIndex: 'GLN',
                            key: 'GLN',
                            className: 'restrictShrink',
                            render: GLN => {
                              return <span>{GLN || ''}</span>
                            },
                          }
                        : {},
                      {
                        title: 'Type',
                        dataIndex: 'type',
                        key: 'type',
                        className: 'restrictShrink',
                      },
                      {
                        title: 'Action',
                        key: 'action',
                        // Always sticky. When Add to basket is also shown, pin
                        // Action immediately left of it (sticky-actions-*-prev).
                        className: isCatalogueProductGate
                          ? 'restrictShrink sticky-actions-col'
                          : 'restrictShrink sticky-actions-col-prev',
                        onCell: () =>
                          isCatalogueProductGate
                            ? { className: 'sticky-actions-cell' }
                            : {
                                className: 'sticky-actions-cell-prev',
                              },
                        render: (text, product) => {
                          const addState = getCatalogueAddButtonState({
                            target: 'list',
                            onShoppingList: venueProductsIds.includes(
                              product._id,
                            ),
                            adding: addingProductId === product._id,
                          })
                          const addButton = (
                            <Button
                              type={'sucsess'}
                              disabled={addState.disabled}
                              onClick={() => handleAddClick(product)}
                            >
                              <span>{addState.label}</span>
                            </Button>
                          )
                          const reportButton = (
                            <ReportProductIssueButton
                              venueId={venueId}
                              product={product}
                              sourceScreen={PRODUCT_ISSUE_SOURCE.CATALOGUE}
                              iconOnly
                              type='dashed'
                              tooltipPlacement='left'
                            />
                          )
                          return (
                            <CatalogueActionCell>
                              {addState.tooltip ? (
                                <Tooltip title={addState.tooltip}>
                                  <span>{addButton}</span>
                                </Tooltip>
                              ) : (
                                addButton
                              )}
                              {reportButton}
                            </CatalogueActionCell>
                          )
                        },
                      },
                      ...(isCatalogueProductGate
                        ? []
                        : [
                            {
                              title: 'Add to basket',
                              key: 'addToBasket',
                              className:
                                'restrictShrink sticky-actions-col has-sticky-prev sticky-basket-col',
                              onCell: () => ({
                                className:
                                  'sticky-actions-cell has-sticky-prev sticky-basket-col',
                              }),
                              render: (_, product) => {
                                if (!venueProductsIds.includes(product._id)) {
                                  return null
                                }
                                return (
                                  <BasketFunctionality
                                    order={product}
                                    venueId={venueId}
                                    venueProducts={venueProducts}
                                  />
                                )
                              },
                            },
                          ]),
                    ],
                    {
                      hiddenKeys: hiddenColumnKeys,
                    },
                  )}
                />
              )
            )}
          </ListCard>

          <Modal
            title='Edit columns'
            visible={columnPickerOpen}
            onCancel={() => setColumnPickerOpen(false)}
            onOk={() => setColumnPickerOpen(false)}
            okText='Done'
            cancelButtonProps={{ style: { display: 'none' } }}
            data-testid='catalogue-columns-modal'
          >
            <p style={{ marginTop: 0 }}>
              Title, Action and Add to basket always stay visible. Tick the
              others you want on this catalogue.
            </p>
            <ColumnPickerList>
              {cataloguePickerRows.map(column => (
                <Checkbox
                  key={column.key}
                  checked={!pickerHiddenKeys.includes(column.key)}
                  onChange={() => handleToggleCatalogueColumn(column.key)}
                >
                  {column.label}
                </Checkbox>
              ))}
            </ColumnPickerList>
          </Modal>

          {!isCatalogueProductGate && (
            <Modal
              visible={showModal !== null}
              confirmLoading={savingProduct}
              destroyOnClose
              onOk={handleOnOk}
              onCancel={() => {
                if (savingProduct) return
                setShowModal(null)
              }}
            >
              <AddVenueProductModal
                setSelectedProduct={setSelectedProduct}
                selectedProduct={selectedProduct}
                setAddToBasket={setAddToBasket}
                addToBasket={addToBasket}
              />
            </Modal>
          )}
        </Fragment>
      )}
    </Wrapper>
  )
}

const HeroIntro = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  max-width: 38rem;
  font-size: 0.95rem;
  line-height: 1.45;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const ListCard = styled.div`
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: ${({ theme }) => theme.spacing.md};
  background: #fff;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  overflow-x: auto;
  box-sizing: border-box;

  .ant-table-thead > tr > th {
    word-break: keep-all;
    overflow-wrap: normal;
    hyphens: none;
    white-space: normal;
  }
`

const TipBanner = styled.div`
  width: 100%;
  margin: 0 0 ${({ theme }) => theme.spacing.md};
  padding: 1rem 1.1rem 1.15rem;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
  background: #fff;
  box-shadow: ${FOOD_SAFETY_SURFACE.CARD_SHADOW};
  display: flex;
  align-items: flex-start;
  gap: 8px;
  box-sizing: border-box;
`

const TipContent = styled.div`
  flex: 1;
  min-width: 0;
`

const TipHeadline = styled.p`
  margin: 0 0 0.45rem;
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const TipText = styled.p`
  margin: 0;
  max-width: 40rem;
  font-size: 0.9rem;
  line-height: 1.45;
  color: ${({ theme }) => theme.colours.bbqBlack || '#262626'};
`

const WhyToggle = styled(Button)`
  margin: 6px 0 0;
  padding: 0 !important;
  height: auto !important;
  font-size: 0.85rem !important;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT} !important;
  text-decoration: underline;

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#000'} !important;
  }
`

const WhyExpanded = styled.div`
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};

  p {
    margin: 0 0 8px;
    max-width: 40rem;
    font-size: 0.85rem;
    line-height: 1.45;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};

    &:last-child {
      margin-bottom: 0;
    }
  }
`

const TipDismiss = styled(Button)`
  color: ${FOOD_SAFETY_SECONDARY_TEXT} !important;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  padding: 0 !important;
  margin: 0;
  border: none;
  box-shadow: none;
  background: transparent !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;

  .anticon {
    font-size: 16px;
    color: ${FOOD_SAFETY_SECONDARY_TEXT};
  }

  &:hover,
  &:focus {
    color: ${({ theme }) => theme.colours.bbqBlack || '#000'} !important;
    background: ${FOOD_SAFETY_SURFACE.LIME_WASH} !important;

    .anticon {
      color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
    }
  }
`

const ResultsToolbar = styled.div`
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
`

const ColumnPickerList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ToolbarHint = styled.span`
  font-size: 13px;
  color: #595959;
`

const EmptyResults = styled.div`
  width: 100%;
  margin: 8px 0 8px;
  padding: 1.5rem 1.1rem;
  text-align: center;
  background: #fff;
  border: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};
  border-radius: ${FOOD_SAFETY_SURFACE.CARD_RADIUS};
`

const EmptyTitle = styled.h3`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: #262626;
`

const CompareBanner = styled.div`
  width: 100%;
  margin: 0 0 16px;
  padding: 14px 16px;
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 8px;
`

const CompareBannerTitle = styled.p`
  margin: 0 0 6px;
  font-size: 14px;
  line-height: 1.4;
  color: #262626;
`

const CompareBannerHint = styled.p`
  margin: 0;
  font-size: 13px;
  line-height: 1.45;
  color: #595959;
`

const EmptyActions = styled.div`
  margin-top: 16px;
`

const CatalogueActionCell = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
`

const EmptyBody = styled.p`
  margin: 0;
  font-size: 14px;
  line-height: 1.5;
  color: #595959;
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
`

const StyledTable = styled(Table)`
  .ant-pagination-prev .ant-pagination-item-link,
  .ant-pagination-next .ant-pagination-item-link {
    display: flex !important;
    justify-content: center !important;
    align-items: center !important;
  }
`

const Wrapper = styled(FadeIn)`
  width: 100%;
  max-width: none;
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;

  ${({ theme }) => theme.device('md')`
    padding: ${theme.spacing.md};
  `};

  /* Action sits left of Add to basket; keep in sync with .sticky-basket-col */
  --sticky-actions-prev-right: 150px;
  ${stickyActionsColumn}

  .sticky-basket-col {
    min-width: 150px;
    width: 150px;
  }

  .restrictShrink {
    min-width: 120px;
  }

  .diff-symbol {
    position: relative;
    font-size: 13px;
    margin-left: ${({ theme }) => theme.spacing.xxs};
    color: ${({ theme }) => theme.colours.bgGrey};

    &:hover {
      .tooltip {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  }

  .tooltip {
    position: absolute;
    top: 100%;
    left: 50%;
    width: 150px;
    background: white;
    text-align: center;
    border: 1px solid ${({ theme }) => theme.colours.panSilver};
    border-radius: 5px;
    padding: ${({ theme }) => theme.spacing.xxs};
    color: black;
    z-index: 100;
    opacity: 0;
    pointer-events: none;
    transition: 200ms;
    transform: translate(-50%, 10px);
  }
`

export default Catalogue
